import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px', 
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  }
};

const typeTicketOptions = [
  { value: 'VIP', label: 'VIP' },
  { value: 'Standard', label: 'Standard' }
];

function Form({ open, onClose, initialData = null, onSubmitSuccess }) {
  const [formData, setFormData] = React.useState({
    nom: '',
    prenom: '',
    telephone: '',
    email: '',
    type_ticket: ''
  });

  React.useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    } else {
      setFormData({
        nom: '',
        prenom: '',
        telephone: '',
        email: '',
        type_ticket: ''
      });
    }
  }, [initialData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      if (onSubmitSuccess) {
        onSubmitSuccess(formData);
      }
      onClose();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      nom: '',
      prenom: '',
      telephone: '',
      email: '',
      type_ticket: ''
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier l\'invité' : 'Nouvel invité'}</span>
        <IconButton 
          onClick={handleClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              size="small"
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Prénom"
              name="prenom"
              value={formData.prenom}
              onChange={handleChange}
              required
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Téléphone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              required
              size="small"
            />

            <TextField
              sx={{
                width: { xs: '100%', md: '48%' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              size="small"
            />
          </Box>

          <TextField
            fullWidth
            select
            label="Type de ticket"
            name="type_ticket"
            value={formData.type_ticket}
            onChange={handleChange}
            required
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          >
            {typeTicketOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={handleClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func,
  initialData: PropTypes.shape({
    nom: PropTypes.string,
    prenom: PropTypes.string,
    telephone: PropTypes.string,
    email: PropTypes.string,
    type_ticket: PropTypes.string
  })
};

export default Form;